/**
 * Border inner extents — how far the active border's innermost
 * stroke reaches into the sheet, per edge (Princewill, 2026-11-04:
 * "the borderline is colliding with the write-up of the design").
 *
 * A border must never cover a write-up. The renderer clamps the
 * DEFAULT positions of the header write-ups (bank block, holder
 * block, top security statement) clear of these extents through the
 * shared layout; user-dragged positions still win. The values mirror
 * the two border renderers' geometry — BorderlineEngine (the SVG
 * overlay styles) and utils/canvasBorder (the five canvas-drawn
 * styles, whose insets match their SVG twins) — taking the
 * innermost stroke's reach, rounded up. m = max(3, thickness), the
 * engines' own minimum-weight rule.
 *
 * ORNATE's corner flourishes live inside the corner squares
 * (x < 40 on the left edge): the extent returned is its frame
 * reach (11); the header blocks start at x = 40, clear of the
 * flourish zone horizontally.
 */
export interface BorderExtents { top: number; right: number; bottom: number; left: number; }

export function borderInnerExtents(style: string | null | undefined, thickness?: number): BorderExtents {
    const m = Math.max(3, thickness ?? 3);
    // USAA specimen frame: heavy double rules on the TOP and BOTTOM
    // edges only (bottom rules reach 24 in from the edge; the sides
    // carry nothing).
    if ((style || '').toUpperCase() === 'DOUBLE_RULE_BOLD') {
        return { top: 10, right: 0, bottom: 24, left: 0 };
    }
    let e = 0;
    switch ((style || 'NONE').toUpperCase()) {
        case 'NONE': e = 0; break;
        case 'SOLID': e = m + 4; break;              // inner hairline at m+3
        case 'DOUBLE': e = m + 7; break;             // second frame at m+5
        case 'DOTTED': e = m + 5; break;             // inner frame at m+4
        case 'BRAIDED': e = Math.ceil(3.5 * m) + 1; break; // inner rail at 3.5m
        case 'ROPE': e = Math.ceil(3.5 * m) + 1; break;    // inner rail at 3.5m (both renderers)
        case 'SCALLOPED': e = 3 * m + 1; break;      // pattern band reach
        case 'ORNATE': e = 11; break;                // inner frame at 9 + stroke
        case 'MICROPRINT': e = Math.ceil(2.8 * m) + 2; break; // inner hairline past the text path
        case 'ART_DECO': e = 18; break;              // inner rail at ps/2+8 (ps=18)
        case 'GUILLOCHE': e = 21; break;             // inner rail at ps/2+8 (ps=24)
        case 'TREASURY': e = 20; break;              // hairline at band/2+11 (band=16)
        case 'COMMERCIAL': e = 15; break;            // accent line at lw/2+9 (lw=10)
        case 'FILIGREE': e = 24; break;              // inner frame at ps/2+8 (ps=30)
        case 'DOUBLE_RULE': e = m + 10; break;       // second hairline at m+9.5
        case 'GREEK_KEY': e = 22; break;             // inner rail at 6+u+3 (u=12)
        case 'DIAMOND_CHAIN': e = 21; break;         // inner rail past the chain band
        case 'BEAD': e = 16; break;                  // inner rail past the bead band
        case 'MICROPRINT_FRAME': e = 14; break;      // inner rule at 13.5 (GreenFi specimen frame)
        default: e = m + 7; break;                   // unknown style: conservative frame reach
    }
    return { top: e, right: e, bottom: e, left: e };
}
